import { useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronRight, GitBranch, History, Pencil, Plus, Trash2, X } from 'lucide-react'
import { tokens } from '@/theme'
import type { ActivityEntry, Evidence, Initiative, TeamMember } from '@/domain/types'
import { EVIDENCE_KINDS } from '@/domain/types'
import { useInitiative } from '@/api/hooks/useInitiative'
import { useDeleteInitiative } from '@/api/hooks/useDeleteInitiative'
import { useGateReadiness } from '@/api/hooks/useGates'
import {
  ACTIVITY_LIMIT_MAX,
  useActivity,
  useComments,
  useEvidence,
  useEvidenceActions,
  useTeam,
  useTeamActions,
} from '@/api/hooks/useInitiativeChildren'
import { useLookupUsers } from '@/api/hooks/useLookupUsers'
import { useAuthContext } from '@/auth/AuthProvider'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { SkeletonList, ErrorPanel } from '@/components/feedback'
import { StagePill } from '@/components/StagePill'
import { GatePanel } from '@/components/GatePanel'
import { ActivityFeed } from '@/components/ActivityFeed'
import { CommentBox } from '@/components/CommentBox'
import { canShape } from '@/domain/shapeAuthority'
import { isGateAgentAuthor } from '@/domain/gateAgent'
import { stageNeedsEvidence, stageNeedsTeam } from '@/domain/funnelGates'
import { EditInitiativeForm } from './EditInitiativeForm'

interface Props {
  initiativeId: number
  onBack: () => void
  onSelectDomain: (domainId: number) => void
  onSelectCapability: (capabilityId: number) => void
  onSelectInitiative: (initiativeId: number) => void
}

/**
 * Detail page for a single initiative.
 *
 * Top to bottom: header (stage, lifecycle, BU, owner), the pending gate,
 * then only the sections the pending gate actually needs — team while it is
 * Shaped, evidence while a gate is asking for it — comments, and the
 * activity feed behind a button.
 *
 * The shape is deliberately "what has to happen next" rather than "every
 * field this record holds". The funnel record panel that used to list all
 * twenty-odd fields is gone: the gate panel already names what is missing,
 * and the form is where they get filled in, so the read-only transcription
 * of them in between was a third copy nobody read. Everything it showed is
 * still on the record and still on the edit form.
 *
 * The Stage 8 "Dates" panel is gone with `startDate` and `actualEndDate` —
 * both now derive from the gate log, and a hand-typed copy of a derived
 * value is a second source of truth waiting to disagree. What replaced it
 * is the stage time-box, shown in the header when a stage has one.
 *
 * A single `refreshKey` drives every child re-fetch. Gate clears, comments,
 * evidence and team changes all touch the activity feed, so refreshing them
 * independently would leave the feed stale after three of the four.
 */
export function InitiativeDetailView({
  initiativeId,
  onBack,
  onSelectDomain,
  onSelectCapability,
  onSelectInitiative,
}: Props) {
  const [refreshKey, setRefreshKey] = useState(0)
  const refresh = useCallback(() => setRefreshKey(k => k + 1), [])

  const query = useInitiative(initiativeId, {}, refreshKey)
  const gates = useGateReadiness(initiativeId, refreshKey)
  const comments = useComments(initiativeId, refreshKey)
  const evidence = useEvidence(initiativeId, refreshKey)
  const team = useTeam(initiativeId, refreshKey)

  const { authState } = useAuthContext()
  const userOid = authState.status === 'authenticated' ? authState.account.localAccountId : null
  const roles = authState.status === 'authenticated' ? authState.roles : null
  const isAdministrator = roles?.isAdministrator === true

  /**
   * Edit affordances follow the initiative's stage, not the role alone — a
   * plain Reader may shape while it is pre-Gate-1 and not after. Computed
   * per-render because it depends on loaded data; `false` until the
   * initiative arrives, so nothing renders on a guess.
   */
  const canShapeThis =
    roles !== null && query.status === 'success' && canShape(roles, query.data.stageCode)

  const [editOpen, setEditOpen] = useState(false)

  /**
   * The agent's duplicate-review comment is not hidden information — it is
   * the same finding the gate panel's assessment banner leads with, named
   * candidate and all. Two copies of it, one of them signed by something
   * that is not a person, made the thread look like a conversation with a
   * robot. The comment itself is untouched on the record and in the feed.
   */
  const visibleComments = useMemo(
    () =>
      comments.status === 'success'
        ? comments.data.filter(comment => !isGateAgentAuthor(comment.authorDisplayName))
        : [],
    [comments],
  )

  return (
    <>
      <Breadcrumb onBack={onBack}>
        <span style={{ color: tokens.ink3 }}>Initiatives</span>
        <ChevronRight size={10} color={tokens.ink4} />
        <span style={{ color: tokens.ink, fontWeight: tokens.weightDemi }}>
          {query.status === 'success' ? query.data.initiativeName : '…'}
        </span>
      </Breadcrumb>

      <div
        className="orion-pad"
        style={{
          maxWidth: tokens.containerMax,
          margin: '0 auto',
          padding: `40px ${tokens.containerPadX}px`,
        }}
      >
        {query.status === 'loading' && <SkeletonList rows={1} rowHeight={220} />}
        {query.status === 'error' && <ErrorPanel error={query.error} />}

        {query.status === 'success' && (
          <>
            <HeaderCard
              initiative={query.data}
              onSelectDomain={onSelectDomain}
              onSelectCapability={onSelectCapability}
              onSelectInitiative={onSelectInitiative}
            />

            {canShapeThis && query.data.isActive && (
              <ContributorControls onEdit={() => setEditOpen(true)} />
            )}

            {gates.status === 'loading' && (
              <div style={{ marginTop: 32 }}>
                <SkeletonList rows={2} rowHeight={54} />
              </div>
            )}
            {gates.status === 'error' && (
              <div style={{ marginTop: 32 }}>
                <ErrorPanel error={gates.error} />
              </div>
            )}
            {gates.status === 'success' && (
              <GatePanel initiativeId={initiativeId} readiness={gates.data} onCleared={refresh} />
            )}

            {/* Team and evidence appear where a gate asks for them and not
                before: at Idea, a "Nobody named yet" panel is a to-do the
                funnel has not set yet. Both keep whatever was recorded
                earlier — it is the section that hides, not the rows. */}
            {stageNeedsTeam(query.data.stageCode) && (
              <TeamSection
                initiativeId={initiativeId}
                members={team.status === 'success' ? team.data : []}
                canEdit={canShapeThis}
                onChanged={refresh}
              />
            )}

            {stageNeedsEvidence(query.data.stageCode, query.data.aiActClassCode) && (
              <EvidenceSection
                initiativeId={initiativeId}
                rows={evidence.status === 'success' ? evidence.data : []}
                canEdit={canShapeThis}
                onChanged={refresh}
              />
            )}

            <section style={{ marginTop: 32 }}>
              <SectionHeader
                label="Comments"
                count={comments.status === 'success' ? visibleComments.length : undefined}
              />
              <CommentBox
                initiativeId={initiativeId}
                comments={visibleComments}
                userOid={userOid}
                isAdministrator={isAdministrator}
                onChanged={refresh}
              />
            </section>

            <ActivitySection initiativeId={initiativeId} refreshKey={refreshKey} />

            {isAdministrator && query.data.isActive && (
              <AdminCorrectionPanel initiative={query.data} onDeleted={onBack} />
            )}

            <EditInitiativeForm
              open={editOpen}
              initiative={query.data}
              onClose={() => setEditOpen(false)}
              onUpdated={() => {
                setEditOpen(false)
                refresh()
              }}
            />
          </>
        )}
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function HeaderCard({
  initiative,
  onSelectDomain,
  onSelectCapability,
  onSelectInitiative,
}: {
  initiative: Initiative
  onSelectDomain: (id: number) => void
  onSelectCapability: (id: number) => void
  onSelectInitiative: (id: number) => void
}) {
  return (
    <div
      style={{
        padding: '28px 32px',
        background: tokens.paper2,
        border: `1px solid ${tokens.line}`,
        borderRadius: tokens.radiusMd,
        fontFamily: tokens.fontDisplay,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ width: 32, height: 2, background: tokens.gold }} />
        <span
          title={initiative.businessUnitName}
          style={{
            fontSize: 11,
            fontWeight: tokens.weightDemi,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: tokens.gold,
          }}
        >
          {initiative.businessUnitDisplayCode}
        </span>
        <StagePill
          stageCode={initiative.stageCode}
          stageName={initiative.stageName}
          lifecycle={initiative.lifecycle}
          size="md"
        />
        {initiative.stageDueDate && (
          <span
            style={{
              fontSize: 10.5,
              fontWeight: tokens.weightDemi,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: initiative.stageOverdue ? tokens.statusAmber : tokens.ink3,
              background: initiative.stageOverdue ? tokens.statusAmberWash : tokens.paper3,
              border: `1px solid ${initiative.stageOverdue ? tokens.statusAmberLine : tokens.line2}`,
              borderRadius: tokens.radiusSm,
              padding: '2px 8px',
            }}
          >
            {initiative.stageOverdue ? 'Time-box passed' : 'Time-box'} {initiative.stageDueDate}
          </span>
        )}
        {!initiative.isActive && (
          <span
            style={{
              fontSize: 10,
              fontWeight: tokens.weightDemi,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: tokens.ink3,
              background: tokens.paper3,
              border: `1px solid ${tokens.line2}`,
              borderRadius: tokens.radiusSm,
              padding: '2px 8px',
            }}
          >
            Deactivated
          </span>
        )}
      </div>

      <h1
        style={{
          fontSize: 30,
          fontWeight: tokens.weightDemi,
          color: tokens.ink,
          margin: '0 0 8px',
          lineHeight: 1.2,
        }}
      >
        {initiative.initiativeName}
      </h1>

      <p
        style={{
          fontSize: tokens.sizeBodyL,
          color: tokens.ink2,
          lineHeight: 1.55,
          maxWidth: 720,
          margin: 0,
          whiteSpace: 'pre-wrap',
        }}
      >
        {initiative.initiativeDescription}
      </p>

      {initiative.lifecycle === 'closed' && (
        <Banner
          tone="closed"
          title={`Closed — ${initiative.closureReason ?? 'no reason recorded'}`}
        >
          {initiative.closureNote}
        </Banner>
      )}
      {initiative.lifecycle === 'blocked' && (
        <Banner
          tone="blocked"
          title={`Blocked — ${initiative.blockedReasonName ?? initiative.blockedReasonCode ?? 'reason not set'}`}
        >
          {initiative.blockedNote}
        </Banner>
      )}

      <div
        style={{
          display: 'flex',
          gap: 16,
          marginTop: 24,
          paddingTop: 20,
          borderTop: `1px solid ${tokens.line}`,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <CrossLink onClick={() => onSelectDomain(initiative.domainId)} label="Domain">
          {initiative.domainName}
        </CrossLink>
        <span style={{ color: tokens.ink4 }}>›</span>
        <CrossLink onClick={() => onSelectCapability(initiative.capabilityId)} label="Capability">
          {initiative.capabilityName}
        </CrossLink>
        {initiative.forkedFromInitiativeId !== null && (
          <>
            <span style={{ color: tokens.ink4 }}>·</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <GitBranch size={12} strokeWidth={1.7} color={tokens.ink3} />
              <CrossLink
                onClick={() => onSelectInitiative(initiative.forkedFromInitiativeId as number)}
                label="Forked from"
              >
                {initiative.forkedFromInitiativeName ?? `#${initiative.forkedFromInitiativeId}`}
              </CrossLink>
            </span>
          </>
        )}
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <MicroLabel>Owner</MicroLabel>
          <span style={{ fontSize: 13, color: tokens.ink2, fontWeight: tokens.weightMedium }}>
            {initiative.ownerDisplayName || '—'}
          </span>
        </span>
      </div>
    </div>
  )
}

function Banner({
  tone,
  title,
  children,
}: {
  tone: 'closed' | 'blocked'
  title: string
  children?: string | null
}) {
  const colour = tone === 'closed' ? tokens.statusRed : tokens.statusAmber
  const wash = tone === 'closed' ? tokens.statusRedWash : tokens.statusAmberWash
  const line = tone === 'closed' ? tokens.statusRedLine : tokens.statusAmberLine
  return (
    <div
      role="note"
      style={{
        marginTop: 18,
        padding: '12px 16px',
        background: wash,
        border: `1px solid ${line}`,
        borderRadius: tokens.radiusMd,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: tokens.weightDemi, color: colour }}>{title}</div>
      {children && (
        <p
          style={{
            margin: '6px 0 0',
            fontSize: 13,
            color: tokens.ink2,
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
          }}
        >
          {children}
        </p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Activity
// ---------------------------------------------------------------------------

/** Entries per press — of "Show activity" and of "Load more" alike. */
const ACTIVITY_PAGE = 5

/**
 * The feed, closed until asked for.
 *
 * Activity is the longest thing on the page and the least often the reason
 * somebody opened it, so it starts as one line and one button. The fetch is
 * lazy in the only way `useApiQuery` supports: the component that issues it
 * is not mounted until the reader asks, which also means a reader who never
 * asks costs no request at all.
 */
function ActivitySection({
  initiativeId,
  refreshKey,
}: {
  initiativeId: number
  refreshKey: number
}) {
  // Null is "not asked yet" rather than 0, so the closed state and an empty
  // page can't be confused.
  const [limit, setLimit] = useState<number | null>(null)

  return (
    <section style={{ marginTop: 32 }}>
      <SectionHeader
        label="Activity"
        action={
          limit === null
            ? undefined
            : { label: 'Hide activity', onClick: () => setLimit(null), icon: null }
        }
      />

      {limit === null ? (
        <button
          type="button"
          onClick={() => setLimit(ACTIVITY_PAGE)}
          style={{ ...compactSecondaryStyle, display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          <History size={13} strokeWidth={1.7} />
          Show activity
        </button>
      ) : (
        <ActivityList
          initiativeId={initiativeId}
          limit={limit}
          refreshKey={refreshKey}
          onMore={() =>
            setLimit(current => Math.min((current ?? 0) + ACTIVITY_PAGE, ACTIVITY_LIMIT_MAX))
          }
        />
      )}
    </section>
  )
}

function ActivityList({
  initiativeId,
  limit,
  refreshKey,
  onMore,
}: {
  initiativeId: number
  limit: number
  refreshKey: number
  onMore: () => void
}) {
  const query = useActivity(initiativeId, limit, refreshKey)

  /**
   * Growing the page re-issues the request, and `useApiQuery` reports
   * `loading` while it is in flight. Holding the last good answer keeps the
   * five entries already on screen on screen — a list that blinks out and
   * comes back longer reads as a bug.
   */
  const [entries, setEntries] = useState<ActivityEntry[]>([])
  useEffect(() => {
    if (query.status === 'success') setEntries(query.data)
  }, [query])

  const loading = query.status !== 'success' && query.status !== 'error'
  // A full page means the server had at least `limit` to give, so there may
  // be more behind it. One short means the end.
  const mayHaveMore = entries.length >= limit && limit < ACTIVITY_LIMIT_MAX

  if (query.status === 'error') return <ErrorPanel error={query.error} maxWidth="100%" />
  if (loading && entries.length === 0) return <SkeletonList rows={3} rowHeight={64} />

  return (
    <>
      <ActivityFeed
        entries={entries}
        emptyLabel="No activity yet — this initiative may have been created moments ago."
      />

      {mayHaveMore && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
          <button
            type="button"
            disabled={loading}
            onClick={onMore}
            style={{ ...compactSecondaryStyle, opacity: loading ? 0.6 : 1 }}
          >
            {loading ? 'Loading…' : `Load ${ACTIVITY_PAGE} more`}
          </button>
          <span style={{ fontSize: 12, color: tokens.ink4, fontFamily: tokens.fontDisplay }}>
            Showing the {entries.length} most recent
          </span>
        </div>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Team
// ---------------------------------------------------------------------------

function TeamSection({
  initiativeId,
  members,
  canEdit,
  onChanged,
}: {
  initiativeId: number
  members: TeamMember[]
  canEdit: boolean
  onChanged: () => void
}) {
  const [adding, setAdding] = useState(false)
  const [entraObjectId, setEntraObjectId] = useState('')
  const [roleNote, setRoleNote] = useState('')
  const users = useLookupUsers()
  const actions = useTeamActions()
  const busy = actions.state.status === 'loading'

  const add = async (): Promise<void> => {
    if (!entraObjectId) return
    const result = await actions.add(initiativeId, entraObjectId, roleNote.trim() || undefined)
    if (result !== null) {
      setEntraObjectId('')
      setRoleNote('')
      setAdding(false)
      onChanged()
    }
  }

  const remove = async (memberId: number): Promise<void> => {
    const result = await actions.remove(initiativeId, memberId)
    if (result !== null) onChanged()
  }

  return (
    <section style={{ marginTop: 32 }}>
      <SectionHeader
        label="Team"
        count={members.length}
        note="Gate 1 needs at least one named member."
        action={
          canEdit && !adding ? { label: 'Add member', onClick: () => setAdding(true) } : undefined
        }
      />

      {members.length === 0 ? (
        <EmptyChild label="Nobody named yet." />
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          {members.map(member => (
            <li
              key={member.memberId}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '7px 12px',
                background: tokens.paper,
                border: `1px solid ${tokens.line}`,
                borderRadius: tokens.radiusMd,
                fontFamily: tokens.fontDisplay,
                fontSize: 13,
                color: tokens.ink2,
              }}
            >
              <span style={{ fontWeight: tokens.weightMedium, color: tokens.ink }}>
                {member.displayName || member.userPrincipalName}
              </span>
              {member.roleNote && (
                <span style={{ fontSize: 11.5, color: tokens.ink3 }}>{member.roleNote}</span>
              )}
              {canEdit && (
                <button
                  type="button"
                  aria-label={`Remove ${member.displayName || member.userPrincipalName}`}
                  onClick={() => void remove(member.memberId)}
                  disabled={busy}
                  style={{
                    display: 'inline-flex',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    color: tokens.ink4,
                    cursor: busy ? 'wait' : 'pointer',
                  }}
                >
                  <X size={13} strokeWidth={2} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <div
          style={{
            marginTop: 12,
            display: 'flex',
            gap: 10,
            alignItems: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          <label style={{ display: 'grid', gap: 5 }}>
            <MicroLabel>Person</MicroLabel>
            <select
              value={entraObjectId}
              onChange={e => setEntraObjectId(e.currentTarget.value)}
              style={{ ...compactInputStyle, minWidth: 220 }}
            >
              <option value="">Select…</option>
              {(users.status === 'success' ? users.data.data : []).map(u => (
                <option key={u.entraObjectId} value={u.entraObjectId}>
                  {u.displayName || u.userPrincipalName}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'grid', gap: 5 }}>
            <MicroLabel>Role note</MicroLabel>
            <input
              type="text"
              value={roleNote}
              onChange={e => setRoleNote(e.currentTarget.value)}
              maxLength={200}
              style={{ ...compactInputStyle, minWidth: 220 }}
            />
          </label>
          <button
            type="button"
            disabled={busy || !entraObjectId}
            onClick={() => void add()}
            style={compactPrimaryStyle}
          >
            {busy ? 'Adding…' : 'Add'}
          </button>
          <button type="button" onClick={() => setAdding(false)} style={compactSecondaryStyle}>
            Cancel
          </button>
        </div>
      )}

      {actions.state.status === 'error' && (
        <div style={{ marginTop: 10 }}>
          <ErrorPanel error={actions.state.error} maxWidth="100%" />
        </div>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Evidence
// ---------------------------------------------------------------------------

function EvidenceSection({
  initiativeId,
  rows,
  canEdit,
  onChanged,
}: {
  initiativeId: number
  rows: Evidence[]
  canEdit: boolean
  onChanged: () => void
}) {
  const [adding, setAdding] = useState(false)
  const [kind, setKind] = useState<(typeof EVIDENCE_KINDS)[number]>('TestResult')
  const [title, setTitle] = useState('')
  const [url, setUrl] = useState('')
  const [note, setNote] = useState('')
  const actions = useEvidenceActions()
  const busy = actions.state.status === 'loading'

  const add = async (): Promise<void> => {
    if (!title.trim()) return
    const result = await actions.add(initiativeId, {
      evidenceKind: kind,
      title: title.trim(),
      ...(url.trim() ? { evidenceUrl: url.trim() } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
    })
    if (result !== null) {
      setTitle('')
      setUrl('')
      setNote('')
      setAdding(false)
      onChanged()
    }
  }

  const remove = async (evidenceId: number): Promise<void> => {
    const result = await actions.remove(initiativeId, evidenceId)
    if (result !== null) onChanged()
  }

  return (
    <section style={{ marginTop: 32 }}>
      <SectionHeader
        label="Evidence"
        count={rows.length}
        note="Gate 2 needs a test result with a link; a high-risk AI Act class needs a compliance review."
        action={
          canEdit && !adding
            ? { label: 'Record evidence', onClick: () => setAdding(true) }
            : undefined
        }
      />

      {rows.length === 0 ? (
        <EmptyChild label="No evidence recorded." />
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
          {rows.map(row => (
            <li
              key={row.evidenceId}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 12,
                alignItems: 'start',
                padding: '12px 16px',
                background: tokens.paper,
                border: `1px solid ${tokens.line}`,
                borderRadius: tokens.radiusMd,
                fontFamily: tokens.fontDisplay,
              }}
            >
              <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                <span style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <span
                    style={{ fontSize: 13.5, fontWeight: tokens.weightDemi, color: tokens.ink }}
                  >
                    {row.title}
                  </span>
                  <span
                    style={{
                      fontSize: 9.5,
                      fontWeight: tokens.weightDemi,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: tokens.ink3,
                      background: tokens.paper3,
                      border: `1px solid ${tokens.line2}`,
                      borderRadius: tokens.radiusSm,
                      padding: '1px 6px',
                    }}
                  >
                    {row.evidenceKind}
                  </span>
                  <span style={{ fontSize: 11, color: tokens.ink4 }}>at {row.stageName}</span>
                </span>
                {row.evidenceUrl && (
                  <a
                    href={row.evidenceUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    style={{ fontSize: 12, color: tokens.gold, wordBreak: 'break-all' }}
                  >
                    {row.evidenceUrl}
                  </a>
                )}
                {row.note && (
                  <span style={{ fontSize: 12.5, color: tokens.ink2, lineHeight: 1.5 }}>
                    {row.note}
                  </span>
                )}
              </div>
              {canEdit && (
                <button
                  type="button"
                  aria-label={`Remove evidence ${row.title}`}
                  onClick={() => void remove(row.evidenceId)}
                  disabled={busy}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${tokens.line2}`,
                    borderRadius: tokens.radiusSm,
                    width: 26,
                    height: 26,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: tokens.ink3,
                    cursor: busy ? 'wait' : 'pointer',
                  }}
                >
                  <Trash2 size={12} strokeWidth={1.7} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <div style={{ marginTop: 12, display: 'grid', gap: 10, maxWidth: 620 }}>
          <label style={{ display: 'grid', gap: 5 }}>
            <MicroLabel>Kind</MicroLabel>
            <select
              value={kind}
              onChange={e => setKind(e.currentTarget.value as (typeof EVIDENCE_KINDS)[number])}
              style={compactInputStyle}
            >
              {EVIDENCE_KINDS.map(k => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'grid', gap: 5 }}>
            <MicroLabel>Title</MicroLabel>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.currentTarget.value)}
              maxLength={200}
              style={compactInputStyle}
            />
          </label>
          <label style={{ display: 'grid', gap: 5 }}>
            <MicroLabel>Link</MicroLabel>
            <input
              type="url"
              value={url}
              onChange={e => setUrl(e.currentTarget.value)}
              placeholder="https://…"
              style={compactInputStyle}
            />
          </label>
          <label style={{ display: 'grid', gap: 5 }}>
            <MicroLabel>Note</MicroLabel>
            <textarea
              value={note}
              onChange={e => setNote(e.currentTarget.value)}
              rows={2}
              maxLength={2000}
              style={{ ...compactInputStyle, resize: 'vertical' }}
            />
          </label>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              disabled={busy || !title.trim()}
              onClick={() => void add()}
              style={compactPrimaryStyle}
            >
              {busy ? 'Recording…' : 'Record'}
            </button>
            <button type="button" onClick={() => setAdding(false)} style={compactSecondaryStyle}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {actions.state.status === 'error' && (
        <div style={{ marginTop: 10 }}>
          <ErrorPanel error={actions.state.error} maxWidth="100%" />
        </div>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Contributor / administrator controls
// ---------------------------------------------------------------------------

function ContributorControls({ onEdit }: { onEdit: () => void }) {
  return (
    <div
      style={{
        marginTop: 24,
        padding: '16px 20px',
        background: tokens.paper2,
        border: `1px solid ${tokens.line}`,
        borderRadius: tokens.radiusMd,
        fontFamily: tokens.fontDisplay,
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        flexWrap: 'wrap',
      }}
    >
      <button
        type="button"
        onClick={onEdit}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          background: tokens.gold,
          color: tokens.paper,
          border: 'none',
          borderRadius: tokens.radiusMd,
          padding: '10px 22px',
          fontFamily: tokens.fontDisplay,
          fontSize: 12,
          fontWeight: tokens.weightDemi,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          cursor: 'pointer',
        }}
      >
        <Pencil size={13} strokeWidth={1.6} />
        Edit the record
      </button>
      <span style={{ fontSize: 12.5, color: tokens.ink3 }}>
        Stage moves happen at a gate, not here.
      </span>
    </div>
  )
}

/**
 * Administrator correction, not retirement.
 *
 * Ordinary retirement is a Close outcome at a gate, and the closed
 * initiative stays on the board in the stage where it died — that
 * visibility is half the point of the registry. Deactivation survives only
 * for genuine mistakes: the duplicate, the test row, the one raised against
 * the wrong business unit.
 */
function AdminCorrectionPanel({
  initiative,
  onDeleted,
}: {
  initiative: Initiative
  onDeleted: () => void
}) {
  const [confirming, setConfirming] = useState(false)
  const { state, mutate, reset } = useDeleteInitiative()
  const isLoading = state.status === 'loading'

  return (
    <div
      style={{
        marginTop: 32,
        padding: '20px 24px',
        background: tokens.paper2,
        border: `1px solid ${tokens.line}`,
        borderRadius: tokens.radiusMd,
        fontFamily: tokens.fontDisplay,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: tokens.weightDemi,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: tokens.ink3,
          marginBottom: 10,
        }}
      >
        Administrator correction
      </div>
      <p
        style={{
          margin: '0 0 14px',
          fontSize: 13.5,
          color: tokens.ink2,
          lineHeight: 1.55,
          maxWidth: 620,
        }}
      >
        Deactivating hides the record. This is for mistakes — a duplicate, a test row, one raised
        against the wrong business unit. To retire real work, close it at a gate so the reason and
        the stage it died in stay on the board.
      </p>

      {!confirming ? (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: 'transparent',
            color: tokens.ink2,
            border: `1px solid ${tokens.line2}`,
            borderRadius: tokens.radiusMd,
            padding: '10px 18px',
            fontFamily: tokens.fontDisplay,
            fontSize: 13,
            fontWeight: tokens.weightMedium,
            cursor: 'pointer',
          }}
        >
          <Trash2 size={14} strokeWidth={1.6} />
          Deactivate this record
        </button>
      ) : (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            disabled={isLoading}
            onClick={() => {
              void mutate(initiative.initiativeId).then(ok => {
                if (ok) onDeleted()
              })
            }}
            style={{
              background: tokens.statusRed,
              color: tokens.paper,
              border: 'none',
              borderRadius: tokens.radiusMd,
              padding: '10px 20px',
              fontFamily: tokens.fontDisplay,
              fontSize: 12,
              fontWeight: tokens.weightDemi,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              cursor: isLoading ? 'wait' : 'pointer',
              opacity: isLoading ? 0.6 : 1,
            }}
          >
            {isLoading ? 'Deactivating…' : 'Yes, deactivate'}
          </button>
          <button
            type="button"
            disabled={isLoading}
            onClick={() => {
              setConfirming(false)
              reset()
            }}
            style={{
              background: 'transparent',
              color: tokens.ink2,
              border: `1px solid ${tokens.line2}`,
              borderRadius: tokens.radiusMd,
              padding: '10px 18px',
              fontFamily: tokens.fontDisplay,
              fontSize: 12,
              fontWeight: tokens.weightMedium,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {state.status === 'error' && (
        <div style={{ marginTop: 12 }}>
          <ErrorPanel error={state.error} />
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Bits
// ---------------------------------------------------------------------------

function SectionHeader({
  label,
  count,
  note,
  action,
}: {
  label: string
  // Explicit `| undefined` rather than `?:` — the project runs with
  // exactOptionalPropertyTypes, so a caller passing a computed
  // `number | undefined` needs the union, not just optionality.
  count?: number | undefined
  note?: string | undefined
  // `icon` defaults to the plus every "add a thing" header wears; pass null
  // for an action that adds nothing.
  action?: { label: string; onClick: () => void; icon?: React.ReactNode } | undefined
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 12,
        marginBottom: 12,
        flexWrap: 'wrap',
      }}
    >
      <h2
        style={{
          fontSize: tokens.sizeH3,
          fontWeight: tokens.weightDemi,
          color: tokens.ink,
          margin: 0,
          fontFamily: tokens.fontDisplay,
        }}
      >
        {label}
      </h2>
      {count !== undefined && (
        <span
          style={{
            fontSize: 12,
            color: tokens.ink3,
            fontFamily: tokens.fontDisplay,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {count}
        </span>
      )}
      {note && (
        <span style={{ fontSize: 12, color: tokens.ink4, fontFamily: tokens.fontDisplay }}>
          {note}
        </span>
      )}
      {action && (
        <>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            onClick={action.onClick}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'transparent',
              color: tokens.ink2,
              border: `1px solid ${tokens.line2}`,
              borderRadius: tokens.radiusMd,
              padding: '6px 12px',
              fontFamily: tokens.fontDisplay,
              fontSize: 11,
              fontWeight: tokens.weightDemi,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            {'icon' in action ? action.icon : <Plus size={12} strokeWidth={2} />}
            {action.label}
          </button>
        </>
      )}
    </div>
  )
}

function CrossLink({
  children,
  onClick,
  label,
}: {
  children: React.ReactNode
  onClick: () => void
  label: string
}) {
  const [hover, setHover] = useState(false)
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <MicroLabel>{label}</MicroLabel>
      <button
        type="button"
        onClick={onClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          fontSize: 13,
          fontFamily: tokens.fontDisplay,
          fontWeight: tokens.weightMedium,
          color: hover ? tokens.gold : tokens.ink2,
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          textDecoration: hover ? 'underline' : 'none',
          textUnderlineOffset: 3,
          transition: `color ${tokens.durFast} ${tokens.easeStandard}`,
        }}
      >
        {children}
      </button>
    </span>
  )
}

function MicroLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: tokens.weightDemi,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: tokens.ink3,
        fontFamily: tokens.fontDisplay,
      }}
    >
      {children}
    </span>
  )
}

function EmptyChild({ label }: { label: string }) {
  return (
    <div
      style={{
        padding: '24px 20px',
        textAlign: 'center',
        color: tokens.ink3,
        fontFamily: tokens.fontDisplay,
        fontSize: 13,
        background: tokens.paper2,
        border: `1px dashed ${tokens.line2}`,
        borderRadius: tokens.radiusMd,
      }}
    >
      {label}
    </div>
  )
}

const compactInputStyle: React.CSSProperties = {
  padding: '8px 11px',
  fontSize: 13,
  fontFamily: tokens.fontDisplay,
  color: tokens.ink,
  background: tokens.paper,
  border: `1px solid ${tokens.line2}`,
  borderRadius: tokens.radiusMd,
  boxSizing: 'border-box',
  width: '100%',
}

const compactPrimaryStyle: React.CSSProperties = {
  background: tokens.gold,
  color: tokens.paper,
  border: 'none',
  borderRadius: tokens.radiusMd,
  padding: '9px 18px',
  fontFamily: tokens.fontDisplay,
  fontSize: 11.5,
  fontWeight: tokens.weightDemi,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  cursor: 'pointer',
}

const compactSecondaryStyle: React.CSSProperties = {
  background: 'transparent',
  color: tokens.ink2,
  border: `1px solid ${tokens.line2}`,
  borderRadius: tokens.radiusMd,
  padding: '9px 16px',
  fontFamily: tokens.fontDisplay,
  fontSize: 11.5,
  fontWeight: tokens.weightMedium,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  cursor: 'pointer',
}
